import { zodResolver } from '@hookform/resolvers/zod';
import {
  Action,
  Alert,
  Badge,
  Modal,
  MultiSelect,
  TextInput,
} from 'corso-admin-ui';
import {
  CrewMerchantUi,
  EspMetric,
  isTruthy,
  klaviyoEspMetricName,
} from 'corso-types';
import { FormEventHandler, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { match } from 'ts-pattern';
import { useKlaviyoEventTest } from '~/hooks/useIntegrationSettings';
import { useStoreId } from '~/providers/StoreUserProvider';
import { useUser } from '~/providers/UserProvider';

const metricOptions = Object.values(EspMetric).map((metric) => ({
  value: metric,
  label: klaviyoEspMetricName[metric],
}));

const resultVariant = (
  status: CrewMerchantUi.KlaviyoTestEventResult['status'],
) =>
  match(status)
    .with('sent', () => 'success' as const)
    .with('skipped', () => 'warning' as const)
    .with('failed', () => 'danger' as const)
    .exhaustive();

export function KlaviyoTestEventForm({
  showForm,
  closeForm,
  isOrderTrackingEnabled,
}: {
  showForm: boolean;
  closeForm: () => void;
  isOrderTrackingEnabled: boolean;
}) {
  const { mutateAsync: sendTestEvents, isPending } = useKlaviyoEventTest();
  const [results, setResults] = useState<
    CrewMerchantUi.KlaviyoTestEventResult[]
  >([]);

  const {
    handleSubmit,
    formState: { errors, isDirty },
    register,
    reset,
    control,
  } = useForm({
    resolver: zodResolver(CrewMerchantUi.klaviyoTestEventSchema),
    defaultValues: { metrics: [], claimExternalId: '' },
  });

  const options = isOrderTrackingEnabled
    ? metricOptions
    : metricOptions.filter(
        (option) => option.value !== EspMetric.orderShipmentUpdate,
      );

  const closeHandler = () => {
    closeForm();
    reset();
    setResults([]);
  };

  const user = useUser();
  const storeId = useStoreId();
  const submitHandler: FormEventHandler<HTMLFormElement> = (event) => {
    handleSubmit(
      (data) => sendTestEvents(data).then(setResults).catch(console.error),
      console.error,
    )(event).catch(console.error);
  };

  return (
    <Modal
      show={showForm}
      onClose={closeHandler}
      title="Test Klaviyo Events"
      description="Send test Corso events to your Klaviyo account."
    >
      <form className="flex flex-col gap-6" onSubmit={submitHandler}>
        <Alert
          variant="warning"
          heading={`Events will be sent to ${user.email}`}
        />

        <Controller
          control={control}
          name="metrics"
          render={({ field: { onChange, value }, fieldState }) => (
            <MultiSelect
              togglable
              label="Events"
              details="Select the events to send."
              options={options}
              value={value
                .map((metric) =>
                  options.find((option) => option.value === metric),
                )
                .filter(isTruthy)}
              onChange={(selected) =>
                onChange(selected.map((option) => option.value))
              }
              error={fieldState.error?.message}
            />
          )}
        />

        <TextInput
          id="klaviyo-test-claim-number"
          label="Claim"
          placeholder={`#${storeId}-12345-2`}
          details="Optionally provide a Return or Warranty to test with real data."
          {...register('claimExternalId')}
          error={errors.claimExternalId?.message}
        />

        {results.length > 0 && (
          <div className="flex flex-col gap-2">
            {results.map(({ metric, status, detail }) => (
              <div className="flex items-center gap-2" key={metric}>
                <Badge variant={resultVariant(status)}>{status}</Badge>
                <span className="text-sm">{klaviyoEspMetricName[metric]}</span>
                {detail && (
                  <span className="text-xs text-neutral-500">{detail}</span>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-row justify-end gap-2">
          <Action onClick={closeHandler}>Close</Action>
          <Action
            variant="primary"
            type="submit"
            disabled={!isDirty || isPending}
            loading={isPending}
          >
            Send
          </Action>
        </div>
      </form>
    </Modal>
  );
}
